import { type GetServerSideProps } from 'next'
import { type Session } from 'next-auth'
import { getServerSession } from 'next-auth/next'
import { useSession } from 'next-auth/react'
import { useEffect } from 'react'
import { useTheme } from 'styled-components'
import { v4 as generateUUID } from 'uuid'

import { AppMarketingBanner } from '@/components/homepage/app-marketing-banner/AppMarketingBanner'
import { FeedFallbackComponents } from '@/components/homepage/feed-fallback-components/FeedFallbackComponents'
import { getHomepageInitialLocation } from '@/components/homepage/getHomepageInitialLocation'
import { getCMSContentModule } from '@/components/homepage/hero-cms-banner/getCMSContentModule'
import { HeroCMSBanner } from '@/components/homepage/hero-cms-banner/HeroCMSBanner'
import { HomepageFeed } from '@/components/homepage/homepage-feed/HomepageFeed'
import { getMenuPrefetch } from '@/components/homepage/mega-menu/getMenuPrefetch'
import { MegaMenu } from '@/components/homepage/mega-menu/MegaMenu'
import { PopularOnKijiji } from '@/components/homepage/popular-on-kijiji/PopularOnKijiji'
import { HomepageSeoMetadata } from '@/components/homepage/seo-metadata/HomepageSeoMetadata'
import { SimilarToFavouritesGallery } from '@/components/homepage/similar-to-favourites/SimilarToFavouritesGallery'
import {
  HomepageAside,
  HomepageContainer,
  HomepageMain,
  HomepageSectionWrapper,
} from '@/components/homepage/styled'
import { useHomepageGetSeoUrl } from '@/components/homepage/useHomepageGetSeoUrl'
import { useHomepageInfiniteFeed } from '@/components/homepage/useHomepageInfiniteFeed'
import { ClientRender } from '@/components/shared/client-render'
import { BaseLayout } from '@/components/shared/layouts/BaseLayout'
import { PAGE_TYPE } from '@/constants/pageTypes'
import { getLocale } from '@/domain/locale'
import { isUserAuthenticated } from '@/domain/user'
import {
  type UserLocation,
  GetUserLocaleDocument,
  GetUserLocationDocument,
} from '@/generated/graphql-types'
import { useGetLocation } from '@/hooks/location/useGetLocation'
import { useLocale } from '@/hooks/useLocale'
import { GptRightrailBottom } from '@/lib/ads/homepage-ads/ad-slots/GptRightrailBottom'
import { GptRightrailMarketing } from '@/lib/ads/homepage-ads/ad-slots/GptRightrailMarketing'
import { GptRightrailTop } from '@/lib/ads/homepage-ads/ad-slots/GptRightrailTop'
import { GptStickyHP } from '@/lib/ads/homepage-ads/ad-slots/GptStickyHP'
import { HomepageAdsWrapper } from '@/lib/ads/homepage-ads/HomepageAdsWrapper'
import { addApolloState, initializeApollo } from '@/lib/apollo/apolloClient'
import { useTracking } from '@/lib/ga'
import { Spacing } from '@/ui/atoms/spacing'
import { ScrollToTopButton } from '@/ui/molecules/scroll-to-top-button'
import { extractIpv4 } from '@/utils/extractIpv4'
import { setGlobalLocation } from '@/utils/location/location'
import { serverSideTranslations } from '@/utils/serverSideTranslations'

import { getServerAuthOptions } from '../api/auth/[...nextauth]'

type HomeProps = {
  userAgent: string
  correlationId: string
  session: Session | null

  /**
   * This variable will be returned while we still have the client-side
   * reactiveVar definition of the location
   *
   * This is the variable returned from the homepage URL so the user Location
   * is updated on the entire website.
   *
   * The homepage bases it location on apollo-cache set on the server, so the client-side update of the location
   * won't impact the calls within this page.
   */
  urlModifiedLocation?: UserLocation | null
}

/**
 * The homepage could be called by accessing `/`
 * or paths like: `/h-<location-seo-name>/<locationId>
 *
 * When there are parameters on the homepage path, then the user's location
 * should be switched to that defined location
 */
const Home = ({ correlationId, userAgent, urlModifiedLocation }: HomeProps) => {
  const { location } = useGetLocation()

  const { status: userStatus, data: userData } = useSession()
  useTracking({
    pageType: PAGE_TYPE.HOMEPAGE,
    data: {
      c: {
        c: { id: 0, n: 'Classifieds' },
        l0: { id: 0, n: 'Classifieds' },
      },
      s: { sr: [], sli: 0 },
    },
  })

  // It should never be undefined - we need to fix the types on GlobalLocation
  const locationId = location.id
  const locationArea = location.area || undefined

  const { spacing } = useTheme()
  const { apiLocale } = useLocale()
  const { getSRPUrl, seoLocation } = useHomepageGetSeoUrl({
    locationArea,
    locationId,
  })

  const {
    feedPageInfo,
    feedResults,
    handleLoadMore,
    loadingFeed,
    loadingMore: hpFeedLoadingMore,
    shouldAutoFetchMore,
    shouldShowHomepageFeed,
  } = useHomepageInfiniteFeed({
    correlationId,
    locationArea,
    locationId,
    userAgent,
  })

  useEffect(() => {
    /** This useEffect should be removed one the reactive variable has been removed */
    if (urlModifiedLocation?.id) {
      setGlobalLocation({
        id: urlModifiedLocation.id,
        isRegion: urlModifiedLocation.isRegion,
        name: urlModifiedLocation.name,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlModifiedLocation?.id])

  return (
    <BaseLayout
      showSearchBar
      includeRUMScripts={true}
      rumPageLabel="Homepage New"
      searchBarChildren={
        <Spacing mTop={spacing.default} data-testid="mega-menu-container">
          <MegaMenu locationId={location.id} />
        </Spacing>
      }
    >
      <HomepageSeoMetadata
        seoLocation={seoLocation}
        locationId={locationId}
        locationName={location.name}
      />
      <HomepageAdsWrapper locationId={locationId} shouldShowHomepageFeed={shouldShowHomepageFeed}>
        <ClientRender>
          <GptRightrailBottom />
        </ClientRender>

        <HeroCMSBanner locationId={locationId} />

        <HomepageContainer>
          <HomepageMain>
            <Spacing mBottom={spacing.husky}>
              <SimilarToFavouritesGallery locationId={locationId} />
            </Spacing>

            {!isUserAuthenticated(userStatus) || (!loadingFeed && !feedResults.length) ? (
              <FeedFallbackComponents
                getSeoUrl={getSRPUrl}
                location={location}
                userAgent={userAgent}
              />
            ) : (
              <HomepageSectionWrapper mTop={spacing.husky} mBottom={spacing.husky}>
                <HomepageFeed
                  correlationId={correlationId}
                  feedPageInfo={feedPageInfo}
                  feedResults={feedResults}
                  getSeoUrl={getSRPUrl}
                  handleLoadMore={handleLoadMore}
                  loading={hpFeedLoadingMore}
                  loadingFeed={loadingFeed}
                  locationId={locationId}
                  locationName={location.name[apiLocale]}
                  shouldAutoFetchMore={shouldAutoFetchMore}
                  userAgent={userAgent}
                  userData={userData}
                />
              </HomepageSectionWrapper>
            )}
          </HomepageMain>

          <HomepageAside>
            <ClientRender>
              <GptRightrailTop />
            </ClientRender>

            <PopularOnKijiji seoLocation={seoLocation} />

            <GptRightrailMarketing />
          </HomepageAside>
        </HomepageContainer>

        <HomepageContainer>
          <ClientRender>
            <GptStickyHP />
          </ClientRender>
          {shouldShowHomepageFeed ? (
            <ScrollToTopButton />
          ) : (
            <HomepageSectionWrapper>
              <AppMarketingBanner />
            </HomepageSectionWrapper>
          )}
        </HomepageContainer>
      </HomepageAdsWrapper>
    </BaseLayout>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const gSSPstart = Date.now()
  const { req, res, query } = context

  const forwardedHeaders = {
    'user-agent': req.headers['user-agent'],
    'x-forwarded-for':
      req.headers['x-forwarded-for'] || extractIpv4(req.socket.remoteAddress || ''),
  }

  const requestHeaders = {
    headers: forwardedHeaders,
    cookies: req.cookies,
  }

  const apolloClient = initializeApollo({ requestHeaders })
  const locale = apolloClient.readQuery({ query: GetUserLocaleDocument })
  const { routeLocale, cookieLocale } = getLocale(locale?.userLocale.routeLocale)

  const correlationId = generateUUID()
  const userAgent = req ? req.headers['user-agent'] || '' : navigator.userAgent
  const translations = await serverSideTranslations(routeLocale, ['home'])

  /**
   * This variable will be returned when homepage URL is defined with: "/h-<locationName>/<locationId>"
   * And this should modify the user's location on page load
   * */
  const urlModifiedLocation = getHomepageInitialLocation(query, apolloClient)

  /**
   * Queries we want to pre-fetch on the server to load asap in the page
   * */
  const location = apolloClient.readQuery({ query: GetUserLocationDocument })
  const locationId = urlModifiedLocation?.id || location?.userLocation.id
  await Promise.all([
    getMenuPrefetch({ apolloClient, cookieLocale, locationId }),
    getCMSContentModule({ apolloClient, locationId }),
  ])

  const sessionStart = Date.now()
  const session = await getServerSession(req, res, getServerAuthOptions(req, res))

  if (process.env.ENABLE_DEBUG === 'true') {
    const serverTimingHeader = res.getHeader('Server-Timing')

    res.setHeader(
      'Server-Timing',
      `${serverTimingHeader || ''}server-session;dur=${Date.now() - sessionStart},`
    )
  }

  if (process.env.ENABLE_DEBUG === 'true') {
    const serverTimingHeader = res.getHeader('Server-Timing')

    res.setHeader(
      'Server-Timing',
      `${serverTimingHeader || ''}server-session;dur=${Date.now() - sessionStart},`
    )
  }

  const pageProps: HomeProps = {
    correlationId,
    session,
    urlModifiedLocation,
    userAgent,
    ...translations,
  }

  const props = addApolloState(apolloClient, pageProps)

  if (process.env.ENABLE_DEBUG === 'true') {
    const serverTimingHeader = res.getHeader('Server-Timing')
    res.setHeader(
      'Server-Timing',
      `${serverTimingHeader || ''}server-props;dur=${Date.now() - gSSPstart},`
    )
  }

  return { props }
}

export default Home
